import { PropsWithChildren, useMemo, useState } from 'react';
import { FieldPath, FieldPathValue, FieldValues } from 'react-hook-form';
import { Button } from '@givebutter/primitives/button';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { faBuilding, faCheck, faPlus, faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as ContactForm from 'assets/js/pages/contacts/ContactForm';
import { ContactFormValues } from 'assets/js/pages/contacts/types';
import * as Form from 'components/form';
import { useGivebutterFormContext } from 'components/form';
import { SelectItem, SelectListItem, UseFormSearchableSelect } from 'components/select';
import { UseFormSearchableSelectProps } from 'components/select/searchable-select';
import { ContactType } from 'constants/contacts';
import { useCurrentPossibleContact } from 'customHooks/useCurrentContact';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteCompanies } from 'customHooks/useInfiniteCompanies';
import { useInfiniteContacts } from 'customHooks/useInfiniteContacts';
import { first } from 'lodash';
import { Contact } from 'types/domain';

interface ContactTypeToggle {
    name?: 'type' | 'contactType' | 'softCredit.contactType';
    onChange?: (value: string) => void;
}
const ContactTypeToggle = ({ name = 'type', onChange }: ContactTypeToggle) => {
    const { control, path } = Form.useGivebutterFormContext();
    return (
        <Form.Container>
            <Form.Label name={path(name)}>
                Contact Type
                <Form.LabelAsterisk />
            </Form.Label>
            <Form.UseFormRadioGroup
                className="flex-col gap-4 sm:flex-row"
                name={name}
                control={control}
                onChange={value => onChange?.(value)}
                variant={'card'}
            >
                <RadioGroup.Item value="individual">
                    <RadioGroup.Indicator className="absolute right-6 top-3 inline-block">
                        <FontAwesomeIcon
                            icon={faCheck}
                            className="h-3 w-3 rounded-full bg-blue-500 p-0.5 text-white"
                        />
                    </RadioGroup.Indicator>
                    <Form.Label name={path(name)} htmlFor="individual">
                        <span className="flex h-full items-start gap-2">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-sm">
                                <FontAwesomeIcon icon={faUser} />
                            </span>
                            <span className="flex-1">
                                <span className="block font-semibold">Individual</span>
                                <span className="text-sm font-normal">
                                    Donor, volunteer, or household member.
                                </span>
                            </span>
                        </span>
                    </Form.Label>
                </RadioGroup.Item>
                <RadioGroup.Item value="company">
                    <RadioGroup.Indicator className="absolute right-6 top-3 inline-block">
                        <FontAwesomeIcon
                            icon={faCheck}
                            className="h-3 w-3 rounded-full bg-blue-500 p-0.5 text-white"
                        />
                    </RadioGroup.Indicator>
                    <Form.Label name={path(name)} htmlFor="company">
                        <span className="flex h-full items-start gap-2">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-sm">
                                <FontAwesomeIcon icon={faBuilding} />
                            </span>
                            <span className="flex-1">
                                <span className="block font-semibold">Company</span>
                                <span className="text-sm font-normal">
                                    Organization, company, or business.
                                </span>
                            </span>
                        </span>
                    </Form.Label>
                </RadioGroup.Item>
            </Form.UseFormRadioGroup>
        </Form.Container>
    );
};

interface CompanyPointOfContactProps extends PropsWithChildren {}
const CompanyPointOfContact = ({ children = 'Point of contact' }: CompanyPointOfContactProps) => {
    const { watch, path, setValue, control } = useGivebutterFormContext<ContactFormValues>();

    const rawSelectedContact = watch('selectedContact');

    const selectedContact = rawSelectedContact?.[0];
    const isEphemeralContact = selectedContact?.id === ContactForm.NewContactId;

    return (
        <div className="flex flex-col gap-6">
            <Form.Container>
                <Form.Label name={path('selectedContact')}>{children}</Form.Label>
                {selectedContact?.value && !isEphemeralContact ? (
                    <div className={'flex items-center gap-2'}>
                        <p>
                            {selectedContact.value.first_name} {selectedContact.value.last_name}
                        </p>
                        <Button
                            variant="none"
                            color="primary"
                            onClick={() => setValue('selectedContact', [])}
                        >
                            Edit
                        </Button>
                    </div>
                ) : (
                    <PointOfContactSelect name={'selectedContact'} />
                )}
            </Form.Container>
            {isEphemeralContact && (
                <div className={'flex flex-col gap-4'}>
                    <div className="flex flex-row gap-4">
                        <Form.Container>
                            <Form.Label name={path('pointOfContact.firstName')}>
                                First name
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <div className={'flex gap-2'}>
                                <Form.TextInputRoot
                                    control={control}
                                    name={'pointOfContact.firstName'}
                                    rules={{
                                        required: 'First name is required',
                                    }}
                                >
                                    <Form.TextInputForm placeholder={'First name'} />
                                </Form.TextInputRoot>
                            </div>
                            <Form.Error name={path('pointOfContact.firstName')} />
                        </Form.Container>
                        <Form.Container>
                            <Form.Label name={path('pointOfContact.lastName')}>
                                Last name
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <div className={'flex gap-2'}>
                                <Form.TextInputRoot
                                    control={control}
                                    name={'pointOfContact.lastName'}
                                    rules={{
                                        required: 'Last name is required',
                                    }}
                                >
                                    <Form.TextInputForm placeholder={'Last name'} />
                                </Form.TextInputRoot>
                            </div>
                            <Form.Error name={path('pointOfContact.lastName')} />
                        </Form.Container>
                    </div>
                    <ContactForm.Emails name="pointOfContactEmails" isRequired />
                    <ContactForm.Phones name="pointOfContactPhones" />
                    <ContactForm.Title name="pointOfContact.title" />
                </div>
            )}
        </div>
    );
};

const CompanyName = () => {
    const { control, path } = Form.useGivebutterFormContext<ContactFormValues>();
    return (
        <Form.Container>
            <Form.Label name={path('companyName')}>
                Company name
                <Form.LabelAsterisk />
            </Form.Label>
            <Form.TextInputRoot
                name={'companyName'}
                control={control}
                rules={{
                    required: 'Company name is required',
                }}
            >
                <Form.TextInputForm placeholder={'Company name'} />
            </Form.TextInputRoot>
            <Form.Error name={path('companyName')} />
        </Form.Container>
    );
};

interface CompanySelectProps<
    T extends FieldValues,
    K extends FieldPath<T>,
    P,
    Item extends FieldPathValue<T, K> & SelectItem<P>,
> extends UseFormSearchableSelectProps<T, K, P, Item> {}

const CompanySelect = <
    T extends FieldValues,
    K extends FieldPath<T>,
    P,
    Item extends FieldPathValue<T, K> & SelectItem<P>,
>(
    props: CompanySelectProps<T, K, P, Item>,
) => {
    const { watch } = useGivebutterFormContext<T>();
    const [searchCompanies, setSearchCompanies] = useState('');
    const debouncedSearchContacts = useDebounce(searchCompanies);
    const { data: companiesData, ref: companiesRef } = useInfiniteCompanies({
        query: debouncedSearchContacts,
    });
    const companies = companiesData?.pages ?? [];

    const { control, name } = props;

    const selectedCompany = watch(name) as Item[];

    return (
        <UseFormSearchableSelect
            control={control}
            name={name}
            localSearch={false}
            clearable={true}
            inputProps={{
                placeholder: 'Select existing company or create new',
                onChange: e => {
                    setSearchCompanies(e.currentTarget.value);
                },
                readOnly: first(selectedCompany)?.id === NewContactId,
            }}
            onSelectItem={value => {
                if (!value) {
                    setSearchCompanies('');
                }
            }}
            disabled={props.disabled}
        >
            <SelectListItem
                key={NewContactId}
                value={{
                    id: NewContactId,
                    label: `Creating new company...`,
                }}
            >
                <div className={'flex gap-2'}>
                    <FontAwesomeIcon icon={faPlus} />
                    <p>Create new company</p>
                </div>
            </SelectListItem>
            {companies.map((item: Contact) => (
                <SelectListItem
                    key={item.id}
                    value={{
                        id: item.id.toString(),
                        label: item.company_name ?? 'N/A',
                        value: item,
                    }}
                >
                    <div className={'flex flex-col gap-2'}>
                        <p className="font-medium">
                            {item.company_name ?? <span className="text-gray-400">N/A</span>}
                        </p>
                        {item.primary_email && (
                            <p className="font-thin text-gray-600">{`${item.primary_email}`}</p>
                        )}
                    </div>
                </SelectListItem>
            ))}
            <div ref={companiesRef} />
        </UseFormSearchableSelect>
    );
};

interface PointOfContactSelectProps<
    T extends FieldValues,
    K extends FieldPath<T>,
    P,
    Item extends FieldPathValue<T, K> & SelectItem<P>,
> extends UseFormSearchableSelectProps<T, K, P, Item> {}

const PointOfContactSelect = <
    T extends FieldValues,
    K extends FieldPath<T>,
    P,
    Item extends FieldPathValue<T, K> & SelectItem<P>,
>(
    props: PointOfContactSelectProps<T, K, P, Item>,
) => {
    const { data: currentContact } = useCurrentPossibleContact();
    const { watch } = useGivebutterFormContext<T>();
    const [searchContacts, setSearchContacts] = useState('');
    const debouncedSearchContacts = useDebounce(searchContacts);
    const { data: contactsData, ref: contactsRef } = useInfiniteContacts({
        query: debouncedSearchContacts,
        type: ContactType.Individual,
        nameRequired: true,
    });
    const contacts = useMemo(() => {
        return contactsData?.pages.filter(contact => contact.id !== currentContact?.id) ?? [];
    }, [contactsData?.pages, currentContact?.id]);

    const { control, name } = props;

    const selectedContact = watch(name) as Item[];

    return (
        <UseFormSearchableSelect
            control={control}
            name={name}
            localSearch={false}
            clearable={true}
            inputProps={{
                placeholder: 'Select existing contact or create new',
                onChange: e => {
                    setSearchContacts(e.currentTarget.value);
                },
                readOnly: first(selectedContact)?.id === NewContactId,
            }}
            onSelectItem={value => {
                if (!value) {
                    setSearchContacts('');
                }
            }}
            disabled={props.disabled}
        >
            <SelectListItem
                key={NewContactId}
                value={{
                    id: NewContactId,
                    label: `Creating new contact...`,
                }}
            >
                <div className={'flex gap-2'}>
                    <FontAwesomeIcon icon={faPlus} />
                    <p>Create new contact</p>
                </div>
            </SelectListItem>
            {contacts.map((item: Contact) => {
                const email = item.primary_email ? `- ${item.primary_email}` : '';
                const label = `${item.first_name} ${item.last_name} ${email}`;
                return (
                    <SelectListItem
                        key={item.id}
                        value={{
                            id: item.id.toString(),
                            label: label,
                            value: item,
                        }}
                    >
                        <div className={'flex gap-2'}>
                            <p className="font-medium">
                                {item.first_name} {item.last_name}
                            </p>
                            {item.primary_email && (
                                <p className="font-thin text-gray-600">{`${item.primary_email}`}</p>
                            )}
                        </div>
                    </SelectListItem>
                );
            })}
            <div ref={contactsRef} />
        </UseFormSearchableSelect>
    );
};

const CompanySelectForm = ({ showAddress }: { showAddress?: boolean }) => {
    const { watch } = useGivebutterFormContext<ContactFormValues>();

    const rawSelectedCompany = watch('selectedCompany');

    const selectedCompany = rawSelectedCompany?.[0];
    const isEphemeralCompany = selectedCompany?.id === ContactForm.NewContactId;

    return (
        <>
            <Form.Container>
                <Form.Label name="selectedCompany">Company</Form.Label>
                <CompanySelect name="selectedCompany" />
            </Form.Container>
            {isEphemeralCompany && (
                <>
                    <CompanyName />
                    {showAddress && <ContactForm.Addresses />}
                    <ContactForm.Emails name="companyEmails" />
                    <ContactForm.Phones name="companyPhones" />
                </>
            )}
        </>
    );
};

const NewContactId = '-1';

const Name = CompanyName;
const PointOfContact = CompanyPointOfContact;
const TypeToggle = ContactTypeToggle;
const Select = CompanySelect;
const SelectForm = CompanySelectForm;

export {
    Name,
    PointOfContact,
    TypeToggle,
    Select,
    SelectForm,
    //
    CompanyName,
    CompanyPointOfContact,
    ContactTypeToggle,
    CompanySelect,
    CompanySelectForm,
};
